import { FormikErrors } from 'formik'
import type { ILeadFormModel } from '../../../typings'
import { isValidPhone, updatePhoneNumber } from '@sales-center/common'
import {
  validateEmailCollection,
  validateEmailCollectionAddLead,
} from '@user/components/EmailCollection'
import {
  FieldSettingsMap,
  IEntityFormSetting,
} from '@user/store/entityFormSettings'
import {
  addLeadAddress,
  addLeadAltPhoneNumber,
  addLeadCompany,
  addLeadDivisionId,
  addLeadEmailAddress,
  addLeadFirstName,
  addLeadLastName,
  addLeadLocationTypeId,
  addLeadPhoneNumber,
  EntityFormFieldName,
  locationUnder30PP,
} from '@user/components/Settings/EntityFormSettings'
import {
  buildNameRequirementErrorMessage,
  NAME_REQUIREMENT_OR,
  NameRequirementMode,
} from '@user/components/Settings/EntityFormSettings/config/nameRequirementMode'
import { FieldNames } from '../FieldNames'

/**
 * Maps a name-group `EntityFormFieldName` to the corresponding key on
 * `FieldNames` for this `LocationInformation` instance. Matches
 * `fieldNameForNameField` in `hooks/useData.ts` byte-for-byte — kept as a
 * separate local copy here since this file isn't a hook and importing across
 * the two would be awkward; keep any change in sync with that copy.
 */
function fieldNameForNameField(
  id: EntityFormFieldName,
  fieldNames: FieldNames,
): string {
  switch (id) {
    case addLeadFirstName:
      return fieldNames.firstName
    case addLeadLastName:
      return fieldNames.lastName
    case addLeadCompany:
      return fieldNames.company
    default:
      return ''
  }
}

export function validateLocation(
  fieldNames: FieldNames,
  fieldNamePrefix: string,
  requiredEntitySettings: IEntityFormSetting[],
  settingsMap: FieldSettingsMap,
  model: ILeadFormModel,
  validatePostalCode: (postalCode: string) => string | undefined,
  nameRequirementMode: NameRequirementMode,
) {
  const errors: FormikErrors<ILeadFormModel> = {}

  const addressField = requiredEntitySettings.find(
    (x) => x.fieldId == addLeadAddress,
  )
  if ((addressField && addressField.required) || model.createNewLocation) {
    if (model[fieldNamePrefix + fieldNames.address1] == '')
      errors[fieldNamePrefix + fieldNames.address1] = 'Required'
    if (model[fieldNamePrefix + fieldNames.city] == '')
      errors[fieldNamePrefix + fieldNames.city] = 'Required'
    if (model[fieldNamePrefix + fieldNames.postalCode] == '')
      errors[fieldNamePrefix + fieldNames.postalCode] = 'Required'
    if (model[fieldNamePrefix + fieldNames.state] == '')
      errors[fieldNamePrefix + fieldNames.state] = 'Required'
  }
  if (
    addressField &&
    model.createNewLocation &&
    model[fieldNamePrefix + fieldNames.address1].length > 30
  )
    errors[fieldNamePrefix + fieldNames.address1] = locationUnder30PP
  const [requireFirstName] = settingsMap.get(addLeadFirstName) ?? [true, true]

  const [requireLastName] = settingsMap.get(addLeadLastName) ?? [true, true]

  const [requireCompany] = settingsMap.get(addLeadCompany) ?? [false, true]

  const [requireEmail] = settingsMap.get(addLeadEmailAddress) ?? [false, true]

  const [requirePhone] = settingsMap.get(addLeadPhoneNumber) ?? [false, true]

  const [requireAltPhone] = settingsMap.get(addLeadAltPhoneNumber) ?? [
    false,
    true,
  ]

  const [requiredLocationTypeId] = settingsMap.get(addLeadLocationTypeId) ?? [
    false,
    true,
  ]

  const [requiredDivisionId] = settingsMap.get(addLeadDivisionId) ?? [
    false,
    true,
  ]

  const requiredNameFields: EntityFormFieldName[] = [
    requireFirstName ? addLeadFirstName : null,
    requireLastName ? addLeadLastName : null,
    requireCompany ? addLeadCompany : null,
  ].filter((id): id is EntityFormFieldName => id !== null)

  if (
    nameRequirementMode === NAME_REQUIREMENT_OR &&
    requiredNameFields.length >= 2
  ) {
    const anyFilled = requiredNameFields.some(
      (id) =>
        (
          model[fieldNamePrefix + fieldNameForNameField(id, fieldNames)] ?? ''
        ).trim() !== '',
    )
    if (!anyFilled) {
      errors[fieldNamePrefix + fieldNames.nameGroup] =
        buildNameRequirementErrorMessage(requiredNameFields)
    }
  } else {
    if (requireFirstName) {
      if (model[fieldNamePrefix + fieldNames.firstName] === '')
        errors[fieldNamePrefix + fieldNames.firstName] = 'Required'
    }

    if (requireLastName) {
      if (model[fieldNamePrefix + fieldNames.lastName] === '')
        errors[fieldNamePrefix + fieldNames.lastName] = 'Required'
    }

    if (requireCompany) {
      if (model[fieldNamePrefix + fieldNames.company] === '')
        errors[fieldNamePrefix + fieldNames.company] = 'Required'
    }
  }

  if (requiredLocationTypeId) {
    if (model[fieldNamePrefix + fieldNames.locationTypeId] === '')
      errors[fieldNamePrefix + fieldNames.locationTypeId] = 'Required'
  }

  if (requiredDivisionId) {
    if (model[fieldNamePrefix + fieldNames.divisionId] === '')
      errors[fieldNamePrefix + fieldNames.divisionId] = 'Required'
  }

  const emailErrors = validateEmailCollectionAddLead(
    model[fieldNamePrefix + fieldNames.emailAddresses],
    requireEmail,
    model.externalLocationId,
  )
  if (emailErrors)
    errors[fieldNamePrefix + fieldNames.emailAddresses] = emailErrors

  if (model[fieldNamePrefix + fieldNames.phone] == '') {
    if (requirePhone) errors[fieldNamePrefix + fieldNames.phone] = 'Required'
  } else if (!isValidPhone(model[fieldNamePrefix + fieldNames.phone]))
    errors[fieldNamePrefix + fieldNames.phone] = 'Must be valid phone number'

  const numbers = /^[0-9]+$/
  if (
    model[fieldNamePrefix + fieldNames.phoneExt] &&
    model[fieldNamePrefix + fieldNames.phoneExt].trim().length > 0
  ) {
    if (
      !model[fieldNamePrefix + fieldNames.phoneExt].match(numbers) ||
      model[fieldNamePrefix + fieldNames.phoneExt].length > 5
    )
      errors[fieldNamePrefix + fieldNames.phoneExt] =
        'Please enter a valid phone extension'
  }

  if (model[fieldNamePrefix + fieldNames.altPhone] == '') {
    if (requireAltPhone)
      errors[fieldNamePrefix + fieldNames.altPhone] = 'Required'
  } else {
    if (!isValidPhone(model[fieldNamePrefix + fieldNames.altPhone])) {
      errors[fieldNamePrefix + fieldNames.altPhone] =
        'Must be valid phone number'
    } else {
      if (
        model[fieldNamePrefix + fieldNames.altPhone]?.trim() ===
          model[fieldNamePrefix + fieldNames.phone]?.trim() &&
        !model[fieldNamePrefix + fieldNames.externalLocationId]
      ) {
        errors[fieldNamePrefix + fieldNames.altPhone] =
          'Must be different from primary phone'
      }
    }
  }

  if (model[fieldNamePrefix + fieldNames.altPhoneExt] != '') {
    if (
      !model[fieldNamePrefix + fieldNames.altPhoneExt].match(numbers) ||
      model[fieldNamePrefix + fieldNames.altPhoneExt].length > 5
    )
      errors[fieldNamePrefix + fieldNames.altPhoneExt] =
        'Please enter a valid alternate phone extension'
  }

  if (model[fieldNamePrefix + fieldNames.postalCode].trim() !== '') {
    const errorPostal = validatePostalCode(
      model[fieldNamePrefix + fieldNames.postalCode],
    )
    if (errorPostal)
      errors[fieldNamePrefix + fieldNames.postalCode] = errorPostal
  }

  if (model[fieldNamePrefix + fieldNames.externalLocationId]) {
    validateModelFromExternalLocation(
      model,
      errors,
      fieldNamePrefix,
      fieldNames,
    )
  }

  return errors
}

function validateModelFromExternalLocation(
  values: ILeadFormModel,
  errors: FormikErrors<ILeadFormModel>,
  fieldNamePrefix: string,
  fieldNames: FieldNames,
): void {
  if (
    values[fieldNamePrefix + fieldNames.firstName].trim() !== '' &&
    values[fieldNamePrefix + fieldNames.firstName].length > 15
  )
    errors[fieldNamePrefix + fieldNames.firstName] =
      'First name must be between 1 and 15 characters'
  if (
    values[fieldNamePrefix + fieldNames.lastName].trim() !== '' &&
    values[fieldNamePrefix + fieldNames.lastName].length > 20
  )
    errors[fieldNamePrefix + fieldNames.lastName] =
      'Last name must be between 1 and 20 characters'
  if (
    values[fieldNamePrefix + fieldNames.company].trim() !== '' &&
    values[fieldNamePrefix + fieldNames.company].length > 35
  )
    errors[fieldNamePrefix + fieldNames.company] =
      'Company must be between 1 and 35 characters'
  if (
    values[fieldNamePrefix + fieldNames.phone].trim() !== '' &&
    updatePhoneNumber(values[fieldNamePrefix + fieldNames.phone]).length > 12
  )
    errors[fieldNamePrefix + fieldNames.phone] =
      'Phone number must be between 1 and 12 characters'
  if (
    values[fieldNamePrefix + fieldNames.phoneExt].trim() !== '' &&
    values[fieldNamePrefix + fieldNames.phoneExt].length > 5
  )
    errors[fieldNamePrefix + fieldNames.phoneExt] =
      'Phone extension must be between 1 and 5 characters'
  if (
    values[fieldNamePrefix + fieldNames.altPhone].trim() !== '' &&
    updatePhoneNumber(values[fieldNamePrefix + fieldNames.altPhone]).length > 12
  )
    errors[fieldNamePrefix + fieldNames.altPhone] =
      'Alt Phone number must be between 1 and 12 characters'
  if (
    values[fieldNamePrefix + fieldNames.altPhoneExt].trim() !== '' &&
    values[fieldNamePrefix + fieldNames.altPhoneExt].length > 5
  )
    errors[fieldNamePrefix + fieldNames.altPhoneExt] =
      'Alt Phone extension must be between 1 and 5 characters'
  const emailErrors = validateEmailCollection(
    values[fieldNamePrefix + fieldNames.emailAddresses],
    false,
    true,
  )
  if (emailErrors)
    errors[fieldNamePrefix + fieldNames.emailAddresses] = emailErrors
}
